import { type FormEvent, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { isStaff } from '@foundry/shared';
import { useMe } from '../api/auth.js';
import { ApiError } from '../api/client.js';
import {
  useCreateProject,
  useDeleteProject,
  usePauseProject,
  useProjects,
  useResumeProject,
} from '../api/projects.js';
import type { Project } from '../api/types.js';
import { Alert, Button, buttonClass, Card, Field, Spinner } from '../components/ui.js';
import { ProjectProgress } from '../components/ProjectDashboard.js';
import { ProjectStatusBadge } from '../status.js';

export function ProjectsPage() {
  const { data: user } = useMe();
  const projectsQ = useProjects();
  const create = useCreateProject();
  const pause = usePauseProject();
  const del = useDeleteProject();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState('');

  // Staff don't own founder projects — their home is the console project list.
  if (user && isStaff(user.role)) return <Navigate to="/console" replace />;

  if (projectsQ.isPending) return <Spinner />;
  if (projectsQ.isError) return <Alert>Could not load your projects.</Alert>;

  const mine = projectsQ.data.filter((p) => p.ownerUserId === user?.id);
  const active = mine.find((p) => p.status !== 'archived');
  const paused = mine.filter((p) => p.status === 'archived');

  // A founder who has not yet completed the questionnaire belongs in it, not on
  // this landing page — send them straight there after login. (With no project
  // yet, they get the create form below, which leads into the questionnaire.)
  if (active && active.status === 'questionnaire') {
    return <Navigate to={`/projects/${active.id}/questionnaire`} replace />;
  }

  const onCreate = (event: FormEvent): void => {
    event.preventDefault();
    if (!name.trim()) return;
    create.mutate(name.trim(), {
      onSuccess: (project) => void navigate(`/projects/${project.id}`),
    });
  };
  const limitHit = create.error instanceof ApiError && create.error.code === 'project_limit';

  // Deleting a project is destructive and irreversible, so require the founder
  // to type an explicit phrase to confirm.
  const confirmDelete = (id: string): void => {
    setDeleteId(id);
    setConfirmText('');
  };
  const canConfirmDelete = confirmText.trim().toLowerCase() === 'delete me';

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="font-title text-3xl tracking-wide">Your project</h1>
        <p className="opacity-70">You work on one project at a time.</p>
      </div>

      {active ? (
        <Card>
          <div className="flex items-center justify-between">
            <Link to={`/projects/${active.id}`} className="text-lg font-medium hover:text-primary">
              {active.name}
            </Link>
            <ProjectStatusBadge status={active.status} />
          </div>
          <div className="mt-3">
            <ProjectProgress projectId={active.id} />
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Link to={`/projects/${active.id}`} className={buttonClass('primary')}>
              View
            </Link>
            <Button
              variant="secondary"
              disabled={pause.isPending}
              onClick={() => pause.mutate(active.id)}
            >
              {pause.isPending ? 'Pausing…' : 'Pause'}
            </Button>
            <Button variant="danger" onClick={() => confirmDelete(active.id)}>
              Delete
            </Button>
          </div>
          <p className="mt-3 text-sm opacity-60">
            Pause or delete this project to start a different one.
          </p>
        </Card>
      ) : (
        <Card>
          <form className="flex flex-wrap items-end gap-3" onSubmit={onCreate}>
            <div className="min-w-64 flex-1">
              <Field
                label="Start a new project"
                placeholder="e.g. Timesheet assistant for law firms"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <Button type="submit" disabled={create.isPending || !name.trim()}>
              {create.isPending ? 'Creating…' : 'Create project'}
            </Button>
          </form>
          {limitHit ? (
            <div className="mt-3">
              <Alert>You already have an active project. Pause or delete it first.</Alert>
            </div>
          ) : create.isError ? (
            <div className="mt-3">
              <Alert>Could not create the project. Please try again.</Alert>
            </div>
          ) : null}
        </Card>
      )}

      {paused.length > 0 ? (
        <div>
          <h2 className="mb-2 font-title text-xl tracking-wide">Paused projects</h2>
          <ul className="flex flex-col gap-2">
            {paused.map((project) => (
              <PausedRow
                key={project.id}
                project={project}
                canResume={!active}
                onDelete={() => confirmDelete(project.id)}
              />
            ))}
          </ul>
        </div>
      ) : null}

      {deleteId ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setDeleteId(null)}
        >
          <div
            className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="mb-2 font-title text-xl tracking-wide">Delete this project?</h2>
            <p className="mb-3 text-sm opacity-70">
              This permanently deletes the project and everything in it — questionnaire,
              specification, user stories, and messages. This cannot be undone.
            </p>
            <label className="mb-1 block text-sm font-medium">
              Type <span className="font-mono text-primary">delete me</span> to confirm
            </label>
            <input
              autoFocus
              className="mb-4 w-full rounded-md border border-secondary/20 bg-white px-3 py-2 outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="delete me"
            />
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDeleteId(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                disabled={!canConfirmDelete || del.isPending}
                onClick={() => del.mutate(deleteId, { onSettled: () => setDeleteId(null) })}
              >
                {del.isPending ? 'Deleting…' : 'Delete project'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function PausedRow({
  project,
  canResume,
  onDelete,
}: {
  project: Project;
  canResume: boolean;
  onDelete: () => void;
}) {
  const resume = useResumeProject();
  return (
    <li className="flex flex-col gap-2 rounded-lg bg-white p-3 shadow-sm ring-1 ring-secondary/5">
      <div className="flex items-center gap-3">
        <Link to={`/projects/${project.id}`} className="flex-1 font-medium hover:text-primary">
          {project.name}
        </Link>
        <ProjectStatusBadge status={project.status} />
        <Button
          variant="secondary"
          disabled={!canResume || resume.isPending}
          title={canResume ? undefined : 'Pause your active project first'}
          onClick={() => resume.mutate(project.id)}
        >
          {resume.isPending ? 'Resuming…' : 'Resume'}
        </Button>
        <Button variant="danger" onClick={onDelete}>
          Delete
        </Button>
      </div>
      <ProjectProgress projectId={project.id} />
    </li>
  );
}
